// import { getAuthorizationHeader } from "@tql/auth-react";
import { getDefaultStore } from "jotai";
import type {
	AfterResponseHook,
	BeforeRequestHook as KyBeforeRequestHook,
	Options,
} from "ky";
import ky, { HTTPError } from "ky";
import type { ZodSchema } from "zod";

import { b2cMsalInstance, msalInstance } from "~/app/main/auth-config.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import type { ApiError, ApiResult } from "~/shared/types/api-types";
import { getAuthorizationHeader } from "~/shared/utils/b2c-utils.ts";
import { debugBus } from "~/shared/utils/debug-bus.ts";
import { environmentConfig } from "~/shared/utils/environment-config.ts";
import { extractApiError } from "~/shared/utils/extract-api-error.ts";
import { getDeploymentType } from "~/shared/utils/get-deployment-type.ts";
import { formatZodErrors } from "~/shared/utils/zod-error-formatter.ts";

export type { ApiResult } from "~/shared/types/api-types";

// API Configuration
const apiConfigs = {
	default: {
		prefixUrl: environmentConfig.services.ltlQuoting.url,
		name: "LTL Quoting API",
		scopes: environmentConfig.services.ltlQuoting.scopes,
	},
	traxAr: {
		prefixUrl: environmentConfig.services.traxArApi.url,
		name: "Trax AR API",
		scopes: environmentConfig.services.traxArApi.scopes,
	},
} as const;

const DEFAULT_ERROR_MESSAGE =
	"LTL Portal encountered a problem. We apologize for the inconvenience. Please try your request again or, if the problem persists, contact tsupport@tql.com or (513) 831-2600 ext. 52198 for assistance.";

const SELECTED_CUSTOMER_STORAGE_KEY = "selectedCustomers";

/**
 * Resolves the active customer id with a sessionStorage fallback.
 *
 * The atom is hydrated from sessionStorage in a `useEffect` in
 * `CustomerDataInitializer`. Child effects (e.g. queries fired by RoleGuard
 * or `useIsVendor`) run before parent effects, so the very first external
 * request after mount can fire while the atom is still `null`. Falling back
 * to sessionStorage guarantees we attach `Tql-Identity-Companyid` whenever a
 * customer has already been selected in this tab.
 */
const getActiveCustomerId = (): string | null => {
	const store = getDefaultStore();
	const selectedCustomer = store.get(selectedCustomerAtom);
	if (selectedCustomer?.id) {
		return String(selectedCustomer.id);
	}

	try {
		const raw = globalThis.sessionStorage.getItem(
			SELECTED_CUSTOMER_STORAGE_KEY,
		);
		if (!raw) {
			return null;
		}
		const parsed = JSON.parse(raw) as { id?: string | number } | null;
		return parsed?.id == null ? null : String(parsed.id);
	} catch {
		return null;
	}
};

const setExternalCustomerHeaders: BeforeRequestHook = (request) => {
	const customerId = getActiveCustomerId();

	if (customerId && !request.headers.has("Tql-Identity-Companyid")) {
		request.headers.set("Tql-Identity-Companyid", customerId);
	}

	if (
		request.headers.has("Tql-Identity-Companyid") &&
		!request.headers.has("Tql-Identity-Accounttype")
	) {
		request.headers.set("Tql-Identity-Accounttype", "2");
	}
};

const defaultAuthHook: BeforeRequestHook = async (request) => {
	try {
		const deploymentType = getDeploymentType();
		const isInternal = deploymentType === "internal";
		const instance = isInternal ? msalInstance : b2cMsalInstance;

		const authHeader = await getAuthorizationHeader(instance, {
			scopes: apiConfigs.default.scopes,
		});

		if (authHeader) {
			request.headers.set("Authorization", authHeader.Authorization);
		} else {
			console.warn("No auth header returned");
		}

		// Add Identity Headers if they don't exist
		if (!isInternal) {
			setExternalCustomerHeaders(request);
		}
	} catch (error) {
		console.error(
			"Authentication error in defaultAuthHook:",
			error instanceof Error ? error.message : error,
		);
		// Re-throw authentication errors so they can be handled by retry logic
		if (
			error instanceof Error &&
			(error.message.includes("token_refresh_required") ||
				error.message.includes("must be refreshed"))
		) {
			throw error;
		}
		console.warn(
			"Non-critical authentication error, making request without auth:",
			error,
		);
	}
};

const traxArAuthHook: BeforeRequestHook = async (request) => {
	try {
		const authHeader = await getAuthorizationHeader(msalInstance, {
			scopes: apiConfigs.traxAr.scopes,
		});
		if (authHeader) {
			request.headers.set("Authorization", authHeader.Authorization);
		} else {
			console.warn("No trax-ar auth header returned");
		}
	} catch (error) {
		console.error(
			"Authentication error in traxArAuthHook:",
			error instanceof Error ? error.message : error,
		);
		// Re-throw authentication errors so they can be handled by retry logic
		if (
			error instanceof Error &&
			(error.message.includes("token_refresh_required") ||
				error.message.includes("must be refreshed"))
		) {
			throw error;
		}
		console.warn(
			"Non-critical trax-ar authentication error, making request without auth:",
			error,
		);
	}
};

export type BeforeRequestHook = (request: {
	headers: {
		set: (k: string, v: string) => void;
		has: (k: string) => boolean;
		get: (k: string) => string | null;
	};
}) => void | Promise<void>;

const debugBeforeRequestHook: KyBeforeRequestHook = async (request) => {
	if (!debugBus.isActive) return;
	try {
		const headers: Record<string, string> = {};
		for (const [k, v] of request.headers.entries()) {
			headers[k] = v;
		}

		let body: unknown;
		try {
			if (request.method !== "GET" && request.method !== "HEAD") {
				const cloned = request.clone();
				const contentType = cloned.headers.get("content-type");
				body = contentType?.includes("application/json")
					? await cloned.json()
					: await cloned.text();
			}
		} catch {
			/* ignore body parsing errors */
		}

		debugBus.emitLog({
			id: crypto.randomUUID(),
			timestamp: new Date(),
			type: "request",
			method: request.method,
			url: request.url,
			headers,
			body,
		});
	} catch (error) {
		console.error("Debug log error", error);
	}
};

const debugAfterResponseHook: AfterResponseHook = async (
	_request,
	_options,
	response,
) => {
	if (!debugBus.isActive) return response;
	try {
		const cloned = response.clone();
		let body: unknown;
		try {
			const contentType = cloned.headers.get("content-type");
			body = contentType?.includes("application/json")
				? await cloned.json()
				: await cloned.text();
		} catch {
			/* ignore parsing errors */
		}

		debugBus.emitLog({
			id: crypto.randomUUID(),
			timestamp: new Date(),
			type: "response",
			method: _request.method,
			url: _request.url,
			status: response.status,
			body,
		});
	} catch (error) {
		console.error("Debug log error", error);
	}
	return response;
};

// Create API instances from configuration
const createKyInstance = (
	config: (typeof apiConfigs)[keyof typeof apiConfigs],
	authHook: BeforeRequestHook,
) => {
	return ky.create({
		prefixUrl: config.prefixUrl,
		headers: {
			Accept: "application/json",
			"Content-Type": "application/json",
		},
		hooks: {
			beforeRequest: [authHook, debugBeforeRequestHook],
			afterResponse: [debugAfterResponseHook],
		},
		timeout: false,
		retry: 0, // Disable Ky retries, let React Query handle retries
	});
};

// API Instances
export const api = createKyInstance(apiConfigs.default, defaultAuthHook);
export const traxArApi = createKyInstance(apiConfigs.traxAr, traxArAuthHook);

const buildOptions = <B>(
	body?: B,
	signal?: AbortSignal,
	keepalive?: boolean,
	timeout?: Options["timeout"],
	cache?: RequestCache,
): Options => {
	const options: Options = body === undefined ? {} : { json: body };
	if (signal) {
		options.signal = signal;
	}
	if (keepalive) {
		// Allow the request to outlive the page (unload/pagehide saves). The
		// browser keeps a keepalive fetch in flight even as the document is torn
		// down, unlike a normal fetch which it may abort. Bodies must stay under
		// the ~64KB keepalive cap — fine for the small preference payloads that
		// opt into this.
		options.keepalive = true;
	}
	if (timeout !== undefined) {
		options.timeout = timeout;
	}
	if (cache) {
		options.cache = cache;
	}
	return options;
};

const validateResponse = <T>(
	json: unknown,
	method: string,
	path: string,
	schema: ZodSchema<T>,
): ApiResult<T> => {
	const result = schema.safeParse(json);
	if (!result.success) {
		const msg = formatZodErrors(result.error.issues);
		console.error(
			`Validation error on ${method.toUpperCase()} ${path}:`,
			result.error,
		);
		return { success: false, error: msg };
	}
	return { success: true, data: result.data };
};

type ApiCallOptions = {
	overrideHook?: BeforeRequestHook;
	instance?: typeof api;
	signal?: AbortSignal;
	keepalive?: boolean;
	timeout?: Options["timeout"];
	/** Bypasses the browser's HTTP cache (e.g. for endpoints whose content can change behind an unchanged URL). */
	cache?: RequestCache;
	onError?: (
		error: unknown,
		context: { method: string; path: string },
	) => Promise<ApiError | null> | ApiError | null;
};

const normalizeApiCallOptions = (
	options?: BeforeRequestHook | ApiCallOptions,
): ApiCallOptions | undefined => {
	if (!options) {
		return undefined;
	}

	if (typeof options === "function") {
		return { overrideHook: options };
	}

	return options;
};

// Some endpoints (e.g. POSTs that ack with 200 + empty body) have no JSON
// payload. Calling .json() on an empty body throws a SyntaxError, so we read
// the body as text first and treat empty/whitespace as undefined. We can't
// clone after calling .json() because the body is already consumed (and the
// debug afterResponse hook may have already cloned this response, which
// further restricts re-reads in some runtimes).
const parseResponseBody = async (response: Response): Promise<unknown> => {
	const text = await response.text();
	if (text.trim() === "") {
		return undefined;
	}
	try {
		return JSON.parse(text);
	} catch (error) {
		if (error instanceof SyntaxError) {
			return text;
		}
		throw error;
	}
};

const AFD_BLOCK_MESSAGE =
	"This request was blocked by our network firewall. Please remove any unusual characters from your input and try again. If the problem persists, contact support with the reference shown in the browser console.";

/**
 * Detects responses that came from Azure Front Door / WAF rather than the
 * upstream API. AFD block pages are HTML and typically arrive as 403 (WAF
 * block) or 503 (no healthy backend / rule trip). Surfacing a non-retryable,
 * user-friendly error here prevents the UI from looping refetches and lets us
 * log the AFD reference headers for incident triage.
 */
const detectAfdBlock = (
	error: unknown,
	method: string,
	path: string,
): ApiError | null => {
	if (!(error instanceof HTTPError)) {
		return null;
	}
	const { status } = error.response;
	if (status !== 403 && status !== 503) {
		return null;
	}
	const contentType = error.response.headers.get("content-type") ?? "";
	if (contentType.includes("application/json")) {
		return null;
	}
	const azureRef = error.response.headers.get("x-azure-ref");
	const edgeRef = error.response.headers.get("x-msedge-ref");
	if (azureRef || edgeRef) {
		console.error(`AFD/WAF block on ${method.toUpperCase()} ${path}`, {
			status,
			azureRef,
			edgeRef,
		});
	} else if (status === 403) {
		// 403 with non-JSON body and no AFD ref headers — still likely an
		// edge/WAF block (some configurations strip the headers). Treat the
		// same way to avoid retry storms.
		console.error(
			`Edge block (no AFD ref) on ${method.toUpperCase()} ${path}`,
			{ status },
		);
	} else {
		return null;
	}
	return {
		success: false,
		error: AFD_BLOCK_MESSAGE,
		status,
	};
};

const handleApiCallError = async (
	error: unknown,
	method: string,
	path: string,
	onError: ApiCallOptions["onError"],
): Promise<ApiError> => {
	const afdBlockError = detectAfdBlock(error, method, path);
	if (afdBlockError) {
		return afdBlockError;
	}

	if (onError) {
		try {
			const transformed = await onError(error, { method, path });
			if (transformed) {
				return transformed;
			}
		} catch (handlerError) {
			console.error("Custom API error handler failed:", handlerError);
		}
	}

	const extractedError = await extractApiError(error);
	if (extractedError) {
		return extractedError;
	}

	return { success: false, error: DEFAULT_ERROR_MESSAGE };
};

const apiCall = async <T, B = undefined>(
	method: "get" | "post" | "put" | "patch" | "delete",
	path: string,
	schema: ZodSchema<T>,
	body: B | undefined,
	options?: ApiCallOptions,
): Promise<ApiResult<T>> => {
	const requestOptions = buildOptions(
		body,
		options?.signal,
		options?.keepalive,
		options?.timeout,
		options?.cache,
	);
	const overrideHook = options?.overrideHook;
	const instance = options?.instance;
	const onError = options?.onError;

	if (overrideHook) {
		const headers = new Headers(
			(requestOptions.headers as Record<string, string>) || {},
		);
		await overrideHook({ headers });
		requestOptions.headers = headers;
	}

	const executor = instance ?? api;

	try {
		const response = await executor[method](path, requestOptions);
		const json = await parseResponseBody(response);
		return validateResponse(json, method, path, schema);
	} catch (error) {
		// Don't log AbortErrors as they're expected when requests are cancelled
		if (error instanceof Error && error.name === "AbortError") {
			throw error; // Re-throw to let React Query handle it
		}

		console.error(`API ${method.toUpperCase()} error for ${path}:`, error);

		return handleApiCallError(error, method, path, onError);
	}
};

const createApiClient = (instance: typeof api) => ({
	getAndValidate: <T>(
		path: string,
		schema: ZodSchema<T>,
		options?: BeforeRequestHook | Omit<ApiCallOptions, "instance">,
	): Promise<ApiResult<T>> =>
		apiCall("get", path, schema, undefined, {
			...normalizeApiCallOptions(options),
			instance,
		}),

	postAndValidate: <T, B>(
		path: string,
		body: B,
		schema: ZodSchema<T>,
		options?: BeforeRequestHook | Omit<ApiCallOptions, "instance">,
	): Promise<ApiResult<T>> =>
		apiCall("post", path, schema, body, {
			...normalizeApiCallOptions(options),
			instance,
		}),

	putAndValidate: <T, B>(
		path: string,
		body: B,
		schema: ZodSchema<T>,
		options?: BeforeRequestHook | Omit<ApiCallOptions, "instance">,
	): Promise<ApiResult<T>> =>
		apiCall("put", path, schema, body, {
			...normalizeApiCallOptions(options),
			instance,
		}),

	patchAndValidate: <T, B>(
		path: string,
		body: B,
		schema: ZodSchema<T>,
		options?: BeforeRequestHook | Omit<ApiCallOptions, "instance">,
	): Promise<ApiResult<T>> =>
		apiCall("patch", path, schema, body, {
			...normalizeApiCallOptions(options),
			instance,
		}),

	deleteAndValidate: <T>(
		path: string,
		schema: ZodSchema<T>,
		options?: BeforeRequestHook | Omit<ApiCallOptions, "instance">,
	): Promise<ApiResult<T>> =>
		apiCall("delete", path, schema, undefined, {
			...normalizeApiCallOptions(options),
			instance,
		}),
});

// default API functions (backwards compatibility)
export const getAndValidate = <T>(
	path: string,
	schema: ZodSchema<T>,
	options?: BeforeRequestHook | ApiCallOptions,
): Promise<ApiResult<T>> =>
	apiCall("get", path, schema, undefined, normalizeApiCallOptions(options));

export const postAndValidate = <T, B>(
	path: string,
	body: B,
	schema: ZodSchema<T>,
	options?: BeforeRequestHook | ApiCallOptions,
): Promise<ApiResult<T>> =>
	apiCall("post", path, schema, body, normalizeApiCallOptions(options));

export const putAndValidate = <T, B>(
	path: string,
	body: B,
	schema: ZodSchema<T>,
	options?: BeforeRequestHook | ApiCallOptions,
): Promise<ApiResult<T>> =>
	apiCall("put", path, schema, body, normalizeApiCallOptions(options));

export const patchAndValidate = <T, B>(
	path: string,
	body: B,
	schema: ZodSchema<T>,
	options?: BeforeRequestHook | ApiCallOptions,
): Promise<ApiResult<T>> =>
	apiCall("patch", path, schema, body, normalizeApiCallOptions(options));

export const deleteAndValidate = <T>(
	path: string,
	schema: ZodSchema<T>,
	options?: BeforeRequestHook | ApiCallOptions,
): Promise<ApiResult<T>> =>
	apiCall("delete", path, schema, undefined, normalizeApiCallOptions(options));

// Registry - non-default API calls as <clientName>.getAndValidate(path, schema);
export const apiClients = {
	default: createApiClient(api),
	traxAr: createApiClient(traxArApi),
} as const;

// Convenience exports for specific APIs
export const traxArClient = apiClients.traxAr;

// For backward compatibility, expose TraxAr methods individually
export const getAndValidateTraxAr = traxArClient.getAndValidate;
export const postAndValidateTraxAr = traxArClient.postAndValidate;
export const putAndValidateTraxAr = traxArClient.putAndValidate;
export const patchAndValidateTraxAr = traxArClient.patchAndValidate;
export const deleteAndValidateTraxAr = traxArClient.deleteAndValidate;

export const getTextResponse = async (
	path: string,
	options?: BeforeRequestHook | Omit<ApiCallOptions, "instance">,
): Promise<ApiResult<string>> => {
	try {
		const normalizedOptions = normalizeApiCallOptions(options);
		const requestOptions: Options = {};
		if (normalizedOptions?.overrideHook) {
			const headers = new Headers(
				(requestOptions.headers as Record<string, string>) || {},
			);
			await normalizedOptions.overrideHook({ headers });
			requestOptions.headers = headers;
		}
		if (normalizedOptions?.signal) {
			requestOptions.signal = normalizedOptions.signal;
		}
		const response = await api.get(path, requestOptions);
		const text = await response.text();
		return { success: true, data: text };
	} catch (error) {
		console.error(`API GET text error for ${path}:`, error);
		return { success: false, error: "Network or server error" };
	}
};

export const getBinaryResponse = async (
	path: string,
	options?: BeforeRequestHook | Omit<ApiCallOptions, "instance">,
): Promise<ApiResult<ArrayBuffer>> => {
	try {
		const normalizedOptions = normalizeApiCallOptions(options);
		const requestOptions: Options = {};
		if (normalizedOptions?.overrideHook) {
			const headers = new Headers(
				(requestOptions.headers as Record<string, string>) || {},
			);
			await normalizedOptions.overrideHook({ headers });
			requestOptions.headers = headers;
		}
		if (normalizedOptions?.signal) {
			requestOptions.signal = normalizedOptions.signal;
		}
		if (normalizedOptions?.cache) {
			requestOptions.cache = normalizedOptions.cache;
		}
		const response = await api.get(path, requestOptions);
		const arrayBuffer = await response.arrayBuffer();
		return { success: true, data: arrayBuffer };
	} catch (error) {
		console.error(`API GET binary error for ${path}:`, error);
		return { success: false, error: "Network or server error" };
	}
};

export const postBinaryResponse = async <B>(
	path: string,
	body: B,
	options?: BeforeRequestHook | Omit<ApiCallOptions, "instance">,
): Promise<ApiResult<ArrayBuffer>> => {
	try {
		const normalizedOptions = normalizeApiCallOptions(options);
		const requestOptions: Options = { json: body };
		if (normalizedOptions?.overrideHook) {
			const headers = new Headers(
				(requestOptions.headers as Record<string, string>) || {},
			);
			await normalizedOptions.overrideHook({ headers });
			requestOptions.headers = headers;
		}
		if (normalizedOptions?.signal) {
			requestOptions.signal = normalizedOptions.signal;
		}
		const response = await api.post(path, requestOptions);
		return { success: true, data: await response.arrayBuffer() };
	} catch (error) {
		console.error(`API POST binary error for ${path}:`, error);
		return { success: false, error: "Network or server error" };
	}
};
